import React, { useEffect, useState, useCallback } from "react";
import { ScrollText, Pencil, MessageSquarePlus, UserCheck, Target, Trash2, Upload, X, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PREDICTION_LABELS, VOTE_2022_LABELS, STATUS_LABELS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_META = {
  update: { label: "Güncelleme", icon: Pencil, color: "#0EA5E9" },
  note_added: { label: "Arama Notu", icon: MessageSquarePlus, color: "#10B981" },
  bulk_assign: { label: "Toplu Atama", icon: UserCheck, color: "#0F2C59" },
  target_update: { label: "Hedef Güncelleme", icon: Target, color: "#F59E0B" },
  delete: { label: "Silme", icon: Trash2, color: "#EF4444" },
  import: { label: "İçe Aktarım", icon: Upload, color: "#8B5CF6" },
  restore: { label: "Geri Alma", icon: UserCheck, color: "#22C55E" },
  screenshot_attempt: { label: "Ekran Görüntüsü Denemesi", icon: ShieldAlert, color: "#DC2626" },
};

const FIELD_LABELS = {
  current_vote_prediction_2026: "2026 Tahmini",
  team_assignment_2026: "Sorumlu",
  status: "Durum",
  historical_vote_2022: "2022 Sonucu",
  sector_tags: "Sektörler",
  phone: "Telefon",
  contact_person: "Yetkili",
  text: "Not",
  target_pct: "Hedef %",
  method: "Yöntem",
  page: "Sayfa",
  count: "Adet",
  committee_no: "Komite",
};

function fmtVal(field, v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (field === "current_vote_prediction_2026") return PREDICTION_LABELS[v] || v;
  if (field === "historical_vote_2022") return VOTE_2022_LABELS[v] || v;
  if (field === "status") return STATUS_LABELS[v] || v;
  if (field === "committee_no") return `#${v}`;
  return String(v);
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const ALL = "__all__";

export default function AuditLog() {
  const { committee, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actors, setActors] = useState([]);
  const [f, setF] = useState({ actor: ALL, action: ALL, date_from: "", date_to: "" });
  const [tab, setTab] = useState("audit");
  const [exports, setExports] = useState(null);

  useEffect(() => {
    if (tab === "exports" && exports === null) {
      api.get("/audit/exports", { params: { limit: 300 } })
        .then(({ data }) => setExports(data)).catch(() => setExports([]));
    }
  }, [tab, exports]);

  useEffect(() => { api.get("/audit/actors").then(({ data }) => setActors(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: 300 };
    if (user.role === "super_admin" && committee !== "all") params.committee_no = committee;
    if (f.actor !== ALL) params.actor = f.actor;
    if (f.action !== ALL) params.action = f.action;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    try {
      const { data } = await api.get("/audit", { params });
      setLogs(data);
    } finally {
      setLoading(false);
    }
  }, [committee, user.role, f]);

  useEffect(() => { if (committee !== null) load(); }, [load, committee]);

  const hasFilters = f.actor !== ALL || f.action !== ALL || f.date_from || f.date_to;
  const clearFilters = () => setF({ actor: ALL, action: ALL, date_from: "", date_to: "" });

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">İşlem Günlüğü</h1>
          <p className="text-sm text-muted-foreground">Denetim kaydı — kim, ne zaman, neyi değiştirdi</p>
        </div>
        {user.is_owner && (
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button type="button" onClick={() => setTab("audit")} data-testid="audit-tab-changes"
              className={`px-3 py-1.5 text-xs font-medium ${tab === "audit" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`}>Değişiklikler</button>
            <button type="button" onClick={() => setTab("exports")} data-testid="audit-tab-exports"
              className={`px-3 py-1.5 text-xs font-medium ${tab === "exports" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`}>Dışa Aktarımlar</button>
          </div>
        )}
      </div>

      {tab === "exports" ? (
        exports === null ? (
          <Skeleton className="h-40 rounded-lg" />
        ) : exports.length === 0 ? (
          <Card className="p-12 border border-border text-center text-muted-foreground">Henüz dışa aktarım kaydı yok.</Card>
        ) : (
          <Card className="border border-border divide-y divide-border" data-testid="export-log-list">
            {exports.map((e, i) => (
              <div key={e.id || i} className="flex items-start gap-3 p-3.5 text-sm">
                <Badge variant="secondary" className="text-[11px] uppercase">{e.format || "xlsx"}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{e.path?.replace("/api/", "")}</p>
                  <p className="text-xs text-muted-foreground break-all">
                    {Object.entries(e.filters || {}).filter(([k]) => k !== "format").map(([k, v]) => `${k}=${v}`).join(" · ") || "filtresiz"}
                    {e.rows ? ` · ${e.rows} satır` : ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium text-foreground">{e.actor}</p>
                  <p className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(e.timestamp)}</p>
                  {e.size && <p className="text-[11px] text-muted-foreground">{Math.round(Number(e.size) / 1024)} KB</p>}
                </div>
              </div>
            ))}
          </Card>
        )
      ) : (
      <>
      <Card className="p-4 border border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <Select value={f.actor} onValueChange={(v) => setF({ ...f, actor: v })}>
            <SelectTrigger className="h-9" data-testid="audit-filter-actor"><SelectValue placeholder="Kişi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Kişi: Tümü</SelectItem>
              {actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
            <SelectTrigger className="h-9" data-testid="audit-filter-action"><SelectValue placeholder="İşlem" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>İşlem: Tümü</SelectItem>
              <SelectItem value="update">Güncelleme</SelectItem>
              <SelectItem value="committee_move">Komite Taşıma</SelectItem>
              <SelectItem value="note_added">Arama Notu</SelectItem>
              <SelectItem value="bulk_assign">Toplu Atama</SelectItem>
              <SelectItem value="target_update">Hedef Güncelleme</SelectItem>
              <SelectItem value="delete">Silme</SelectItem>
              <SelectItem value="import">İçe Aktarım</SelectItem>
              <SelectItem value="screenshot_attempt">Ekran Görüntüsü Denemesi</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-9" data-testid="audit-filter-from" />
          <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-9" data-testid="audit-filter-to" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="mt-2 h-7 text-xs" onClick={clearFilters} data-testid="audit-clear-filters">
            <X className="h-3 w-3 mr-1" />Filtreleri Temizle
          </Button>
        )}
      </Card>

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : logs.length === 0 ? (
        <Card className="p-12 border border-border text-center text-muted-foreground">Henüz kayıt yok.</Card>
      ) : (
        <Card className="border border-border divide-y divide-border">
          {logs.map((l, i) => {
            const meta = ACTION_META[l.action] || { label: l.action, icon: ScrollText, color: "#475569" };
            const Icon = meta.icon;
            return (
              <div key={l.id || i} className="flex items-start gap-3 p-4" data-testid="audit-item">
                <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-[11px]">{meta.label}</Badge>
                    <span className="text-sm font-medium text-foreground truncate">{l.entity_name || l.entity_type}</span>
                    {l.committee_no && <span className="text-xs text-muted-foreground">Komite #{l.committee_no}</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {Object.entries(l.changes || {}).map(([field, ch]) => (
                      <span key={field}>
                        <b className="text-foreground/80">{FIELD_LABELS[field] || field}:</b>{" "}
                        {ch && typeof ch === "object" && "new" in ch ? (
                          field === "text" ? <span>"{fmtVal(field, ch.new)}"</span>
                            : <span>{fmtVal(field, ch.old)} → <span style={{ color: meta.color }}>{fmtVal(field, ch.new)}</span></span>
                        ) : String(ch)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium text-foreground">{l.actor}</p>
                  <p className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(l.timestamp)}</p>
                </div>
              </div>
            );
          })}
        </Card>
      )}
      </>
      )}
    </div>
  );
}
